import { RefObject, useMemo, useState } from 'react';

import {
  useLoadRemoteUserSettings,
  useUserSettings,
} from '~/components/UserSettings';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { useGetEmployeeQuickLinksTemp } from '~/services/Http/hooks';

import {
  filterVisibleQuickLinkViewItems,
  getQuickLinksOrder,
  mapCustomQuickLinksToQuickLinkViewItems,
  mapGeneralQuickLinksToQuickLinkViewItems,
  orderQuickLinkViewItems,
} from './data';
import { QuickLinkViewItem } from './models';
import {
  QuickLinksUserSettingsKey,
  resolveQuickLinksUserSettings,
} from './userSettings';

type DialogStatus = {
  state: UseDialogStateOutput;
  enabled: boolean;
};

export interface UseQuickLinksWidgetOutput {
  quickLinkViewItems: {
    all: QuickLinkViewItem[];
    active: QuickLinkViewItem[];
    fitting: QuickLinkViewItem[];
  };
  quickLinksOrder: string[];
  hiddenQuickLinks: string[];
  isLoadingQuickLinks: boolean;
  failedToLoadQuickLinks: boolean;
  setNoOfFittingListItems: (noOfFittingListItems: number) => void;
  configureDialog: DialogStatus;
  showAllDialog: DialogStatus;
  isCompanyWorkspace: boolean;
}

const quickLinksUserSettings = [QuickLinksUserSettingsKey];

export const useQuickLinksWidget = (
  ref: RefObject<HTMLSpanElement>,
  workspaceType?: WorkspaceType,
): UseQuickLinksWidgetOutput => {
  const [noOfFittingListItems, setNoOfFittingListItems] = useState<number>(0);

  const {
    data: generalQuickLinks,
    loading,
    failedToLoad,
  } = useGetEmployeeQuickLinksTemp({ full: true });

  const {
    isLoading: isLoadingCustomQuickLinks,
    failedToLoad: failedToLoadCustomQuickLinks,
  } = useLoadRemoteUserSettings(quickLinksUserSettings);

  const {
    personalWorkspaces: {
      hiddenLinks: hiddenQuickLinks,
      customLinks: customQuickLinks,
      linksOrder: savedQuickLinksOrder,
    },
  } = useUserSettings(resolveQuickLinksUserSettings, QuickLinksUserSettingsKey);

  const configureDialogState = useDialogState();
  const showAllDialogState = useDialogState();

  const isCompanyWorkspace = workspaceType === WorkspaceType.Company;
  const generalQuickLinksOrder = useMemo(
    () =>
      (generalQuickLinks || [])
        ?.sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0))
        ?.map((link) => link.id),
    [generalQuickLinks],
  );

  const generalQuickLinkViewItems: QuickLinkViewItem[] = useMemo(
    () =>
      generalQuickLinks
        ? mapGeneralQuickLinksToQuickLinkViewItems(
            generalQuickLinks.filter(
              (ql) => ql.location === 'quick_links_widget',
            ),
            hiddenQuickLinks,
          )
        : [],
    [generalQuickLinks, hiddenQuickLinks],
  );

  const customQuickLinkViewItems: QuickLinkViewItem[] = useMemo(
    () =>
      mapCustomQuickLinksToQuickLinkViewItems(
        customQuickLinks,
        hiddenQuickLinks,
      ),
    [customQuickLinks, hiddenQuickLinks],
  );

  const quickLinkViewItems = useMemo(() => {
    return isCompanyWorkspace
      ? generalQuickLinkViewItems
      : generalQuickLinkViewItems.concat(customQuickLinkViewItems);
  }, [customQuickLinkViewItems, generalQuickLinkViewItems, isCompanyWorkspace]);

  const quickLinksOrder = useMemo(
    () =>
      isCompanyWorkspace
        ? generalQuickLinksOrder
        : getQuickLinksOrder(quickLinkViewItems, savedQuickLinksOrder),
    [
      savedQuickLinksOrder,
      quickLinkViewItems,
      isCompanyWorkspace,
      generalQuickLinksOrder,
    ],
  );

  const { allQuickLinks, activeQuickLinks, listQuickLinks } = useMemo(() => {
    const combinedLinks = isCompanyWorkspace
      ? generalQuickLinkViewItems
      : generalQuickLinkViewItems.concat(customQuickLinkViewItems);
    const allQuickLinks = orderQuickLinkViewItems(
      combinedLinks,
      quickLinksOrder,
    );

    const activeQuickLinks = filterVisibleQuickLinkViewItems(allQuickLinks);
    const listQuickLinks = activeQuickLinks.slice(0, noOfFittingListItems);

    return {
      allQuickLinks,
      activeQuickLinks,
      listQuickLinks,
    };
  }, [
    generalQuickLinkViewItems,
    customQuickLinkViewItems,
    quickLinksOrder,
    noOfFittingListItems,
    isCompanyWorkspace,
  ]);

  const isLoadingQuickLinks = loading || isLoadingCustomQuickLinks;
  const failedToLoadQuickLinks = failedToLoad || failedToLoadCustomQuickLinks;

  return {
    quickLinkViewItems: {
      all: allQuickLinks,
      active: activeQuickLinks,
      fitting: listQuickLinks,
    },
    quickLinksOrder,
    isLoadingQuickLinks,
    failedToLoadQuickLinks,
    hiddenQuickLinks,
    setNoOfFittingListItems,
    configureDialog: {
      state: configureDialogState,
      enabled: !(isLoadingQuickLinks || failedToLoadQuickLinks),
    },
    showAllDialog: {
      state: showAllDialogState,
      enabled: !(
        isLoadingQuickLinks ||
        failedToLoadQuickLinks ||
        (activeQuickLinks?.length || 0) < noOfFittingListItems
      ),
    },
    isCompanyWorkspace,
  };
};
